import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api-client';

// Types (should ideally come from @gmao/shared)
interface Equipment {
  id: string;
  name: string;
  code: string;
  category_id: string;
  location_id: string;
  status: 'operational' | 'maintenance' | 'out_of_service' | 'retired' | 'reserved';
  criticality: 'low' | 'medium' | 'high' | 'critical';
  manufacturer?: string;
  model?: string;
  serial_number?: string;
  description?: string;
  purchase_date?: string;
  purchase_price?: number;
  supplier?: string;
  purchase_order_number?: string;
  warranty_expiry_date?: string;
  installation_date?: string;
  commissioning_date?: string;
  expected_lifespan_years?: number;
  has_usage_counter?: boolean;
  counter_type?: string;
  current_counter_value?: number;
  photos?: string[];
  created_at: string;
  updated_at: string;
}

interface EquipmentListParams {
  categoryId?: string;
  locationId?: string;
  status?: string;
  criticality?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

interface EquipmentListResponse {
  items: Equipment[];
  total: number;
}

interface EquipmentStats {
  total: number;
  byStatus: Record<string, number>;
  byCriticality: Record<string, number>;
}

// Query keys
export const equipmentKeys = {
  all: ['equipment'] as const,
  lists: () => [...equipmentKeys.all, 'list'] as const,
  list: (filters: EquipmentListParams) => [...equipmentKeys.lists(), filters] as const,
  details: () => [...equipmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...equipmentKeys.details(), id] as const,
  stats: () => [...equipmentKeys.all, 'stats'] as const,
};

// Hooks
export function useEquipmentList(params: EquipmentListParams = {}) {
  return useQuery({
    queryKey: equipmentKeys.list(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (params.categoryId) searchParams.set('category_id', params.categoryId);
      if (params.locationId) searchParams.set('location_id', params.locationId);
      if (params.status) searchParams.set('status', params.status);
      if (params.criticality) searchParams.set('criticality', params.criticality);
      if (params.search) searchParams.set('search', params.search);
      if (params.limit) searchParams.set('limit', params.limit.toString());
      if (params.offset) searchParams.set('offset', params.offset.toString());

      const response = await apiClient.get<EquipmentListResponse>(
        `/equipment?${searchParams.toString()}`
      );
      return response.data;
    },
  });
}

export function useEquipmentDetail(id: string) {
  return useQuery({
    queryKey: equipmentKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<{ equipment: Equipment }>(`/equipment/${id}`);
      return response.data.equipment;
    },
    enabled: !!id,
  });
}

export function useEquipmentStats() {
  return useQuery({
    queryKey: equipmentKeys.stats(),
    queryFn: async () => {
      const response = await apiClient.get<EquipmentStats>('/equipment/stats');
      return response.data;
    },
  });
}

export function useCreateEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Equipment>) => {
      const response = await apiClient.post<{ equipment: Equipment }>('/equipment', data);
      return response.data.equipment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: equipmentKeys.stats() });
    },
  });
}

export function useUpdateEquipment(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Equipment>) => {
      const response = await apiClient.put<{ equipment: Equipment }>(`/equipment/${id}`, data);
      return response.data.equipment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: equipmentKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: equipmentKeys.stats() });
    },
  });
}

export function useDeleteEquipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/equipment/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: equipmentKeys.stats() });
    },
  });
}

// Equipment Documents
interface EquipmentDocument {
  id: string;
  equipment_id: string;
  type: string;
  title: string;
  description?: string;
  file_url: string;
  file_name: string;
  file_size?: number;
  mime_type?: string;
  valid_from?: string;
  valid_until?: string;
  created_at: string;
  created_by?: string;
}

export function useEquipmentDocuments(equipmentId: string) {
  return useQuery({
    queryKey: [...equipmentKeys.detail(equipmentId), 'documents'],
    queryFn: async () => {
      const response = await apiClient.get<{ documents: EquipmentDocument[] }>(
        `/equipment/${equipmentId}/documents`
      );
      return response.data.documents;
    },
    enabled: !!equipmentId,
  });
}

// Equipment Usage History
interface UsageReading {
  id: string;
  equipment_id: string;
  counter_type: string;
  value: number;
  reading_date: string;
  source: string;
  notes?: string;
  created_by?: string;
  created_at: string;
}

export function useEquipmentUsageHistory(equipmentId: string) {
  return useQuery({
    queryKey: [...equipmentKeys.detail(equipmentId), 'usage-history'],
    queryFn: async () => {
      const response = await apiClient.get<{ readings: UsageReading[] }>(
        `/equipment/${equipmentId}/usage-history`
      );
      return response.data.readings;
    },
    enabled: !!equipmentId,
  });
}
